import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Star,
  Eye,
  MapPin,
  Users,
  CheckCircle,
  X,
  Building2,
  Plus,
  Loader2,
  MessageCircle,
  Mail,
  Crown,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import CareRequestsProvider from '@/components/CareRequestsProvider';

const ProviderDashboard = () => {
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('requests');

  const [branches, setBranches] = useState([]);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [branchForm, setBranchForm] = useState({
    business_name: '',
    phone: '',
    address: '',
    municipio: '',
    region: ''
  });
  const [savingBranch, setSavingBranch] = useState(false);
  const [directCount, setDirectCount] = useState(0);
  const [publicCount, setPublicCount] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const profileRes = await api.get('/providers/my-profile');
      setProvider(profileRes.data);

      try {
        const brRes = await api.get('/providers/my-branches');
        setBranches(brRes.data || []);
      } catch {}

      try {
        const crRes = await api.get('/contact-requests/received');
        setDirectCount((crRes.data || []).filter(r => r.status === 'pending').length);
      } catch {}

      try {
        const reqRes = await api.get('/care-requests');
        setPublicCount((reqRes.data || []).length);
      } catch {}
    } catch (error) {
      if (error.response?.status === 401) navigate('/login');
      else if (error.response?.status === 404) navigate('/registrar-residencia');
    } finally {
      setLoading(false);
    }
  };

  const isPremium =
    provider?.plan_active === true &&
    ['premium', 'premium_plus'].includes(provider?.plan_type);

  const planLabel =
    provider?.plan_type === 'premium_plus'
      ? 'Premium+'
      : provider?.plan_type === 'premium'
        ? 'Premium'
        : provider?.plan_type === 'destacado'
          ? 'Destacado'
          : 'Sin plan';

  const resetBranchForm = () => {
    setBranchForm({
      business_name: '',
      phone: '',
      address: '',
      municipio: '',
      region: ''
    });
  };

  const createBranch = async () => {
    if (!branchForm.business_name.trim()) {
      toast.error('El nombre de la sucursal es obligatorio');
      return;
    }

    setSavingBranch(true);
    try {
      const res = await api.post('/providers/my-branches', branchForm);
      toast.success('Sucursal creada exitosamente');
      setBranches(prev => [
        ...prev,
        {
          ...branchForm,
          provider_id: res.data.provider_id,
          created_at: new Date().toISOString()
        }
      ]);
      setShowBranchForm(false);
      resetBranchForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear sucursal');
    } finally {
      setSavingBranch(false);
    }
  };

  const deleteBranch = async (branchId) => {
    if (!window.confirm('¿Eliminar esta sucursal?')) return;

    try {
      await api.delete(`/providers/my-branches/${branchId}`);
      setBranches(prev => prev.filter(b => b.provider_id !== branchId));
      toast.success('Sucursal eliminada');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">No tienes perfil de proveedor</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f8fb] py-6" data-testid="provider-dashboard">
      <div className="max-w-6xl mx-auto px-4">

        <div className="bg-white rounded-3xl border shadow-sm p-5 md:p-6 mb-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold uppercase tracking-wide text-[#000000] bg-[#ffff00] px-3 py-1 rounded-full">
                  Panel del Servicio
                </span>
                {isPremium && (
                  <span className="text-xs font-bold uppercase tracking-wide text-amber-700 bg-amber-50 px-3 py-1 rounded-full flex items-center gap-1">
                    <Crown className="w-3 h-3" />
                    {planLabel} activo
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl font-black text-[#000000] leading-tight">
                {provider.business_name}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-gray-600">
                {provider.verified && (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Resolución Sanitaria
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-amber-500" />
                  {provider.rating?.toFixed(1) || '0.0'} ({provider.total_reviews || 0})
                </span>
                {[provider.municipio, provider.region].filter(Boolean).length > 0 && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    {[provider.municipio, provider.region].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Link to="/chat">
                <Button className="w-full sm:w-auto bg-[#000000] hover:bg-[#222222] text-white rounded-xl">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Ver mensajes
                </Button>
              </Link>
              <Link to={`/provider/${provider.provider_id}`}>
                <Button className="w-full sm:w-auto rounded-xl bg-[#ffff00] text-[#000000] hover:bg-[#e6e600] border border-[#ffff00] shadow-sm">
                  <Eye className="w-4 h-4 mr-2" />
                  Perfil público
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-5">
          <Link to="/chat" className="relative group bg-white rounded-2xl border p-5 shadow-sm hover:shadow-md transition-all">
            {directCount > 0 && (
              <span className="absolute top-4 right-4 min-w-6 h-6 px-2 rounded-full bg-red-500 text-white text-xs font-black flex items-center justify-center">
                {directCount > 9 ? '9+' : directCount}
              </span>
            )}
            <div className="flex items-center justify-between mb-4">
              <div className="w-11 h-11 rounded-2xl bg-[#000000] text-white flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#cccc00]" />
            </div>
            <h3 className="font-black text-[#000000]">Solicitudes Directas</h3>
            <p className="text-sm text-gray-500 mt-1">
              Los contactos directos se responden desde el chat.
            </p>
          </Link>

          <button
            type="button"
            onClick={() => setActiveTab('requests')}
            className="relative text-left bg-white rounded-2xl border p-5 shadow-sm hover:shadow-md transition-all"
          >
            {publicCount > 0 && (
              <span className="absolute top-4 right-4 min-w-6 h-6 px-2 rounded-full bg-[#ffff00] text-[#000000] text-xs font-black flex items-center justify-center">
                {publicCount > 9 ? '9+' : publicCount}
              </span>
            )}
            <div className="w-11 h-11 rounded-2xl bg-[#000000] text-white flex items-center justify-center mb-4">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="font-black text-[#000000]">Oportunidades Públicas</h3>
            <p className="text-sm text-gray-500 mt-1">
              Solicitudes abiertas de tutores buscando servicios para sus mascotas.
            </p>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('branches')}
            className="relative text-left bg-white rounded-2xl border p-5 shadow-sm hover:shadow-md transition-all"
          >
            {branches.length > 0 && (
              <span className="absolute top-4 right-4 min-w-6 h-6 px-2 rounded-full bg-gray-100 text-[#000000] text-xs font-black flex items-center justify-center">
                {branches.length > 9 ? '9+' : branches.length}
              </span>
            )}
            <div className="w-11 h-11 rounded-2xl bg-[#000000] text-white flex items-center justify-center mb-4">
              <Building2 className="w-5 h-5" />
            </div>
            <h3 className="font-black text-[#000000]">Sucursales</h3>
            <p className="text-sm text-gray-500 mt-1">
              {branches.length} sucursal{branches.length === 1 ? '' : 'es'} registrada{branches.length === 1 ? '' : 's'}.
            </p>
          </button>
        </div>

        <div className={`mb-5 rounded-2xl border p-4 ${isPremium ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'}`}>
          <p className={`text-sm font-semibold ${isPremium ? 'text-emerald-800' : 'text-amber-800'}`}>
            Tu plan: {planLabel}{provider?.plan_active ? ' activo' : provider?.plan_type ? ' inactivo' : ''}
          </p>
          <p className={`text-xs mt-1 ${isPremium ? 'text-emerald-700' : 'text-amber-700'}`}>
            {isPremium
              ? 'Puedes recibir solicitudes directas por chat y responder oportunidades públicas.'
              : 'Para activar oportunidades y mayor visibilidad, contacta a PetAdvisor.'}
            {' '}
            <Mail className="w-3 h-3 inline mx-1" />
            <a href="mailto:hola@petadvisor.es" className="underline font-semibold">
              hola@petadvisor.es
            </a>
          </p>
        </div>

        <div className="flex gap-2 mb-5 overflow-x-auto">
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-5 py-3 rounded-xl text-sm font-bold transition-colors whitespace-nowrap ${
              activeTab === 'requests'
                ? 'bg-[#000000] text-white'
                : 'bg-white border text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="tab-requests"
          >
            Oportunidades Públicas
          </button>

          <button
            onClick={() => setActiveTab('branches')}
            className={`px-5 py-3 rounded-xl text-sm font-bold transition-colors whitespace-nowrap ${
              activeTab === 'branches'
                ? 'bg-[#000000] text-white'
                : 'bg-white border text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="tab-branches"
          >
            Sucursales
          </button>
        </div>

        {activeTab === 'requests' && (
          <div className="bg-white rounded-3xl border shadow-sm p-4 md:p-6">
            <div className="mb-5">
              <h2 className="text-xl font-black text-[#000000]">Oportunidades Públicas</h2>
              <p className="text-sm text-gray-500 mt-1">
                Tutores que publicaron una solicitud abierta para recibir propuestas.
              </p>
            </div>

            <CareRequestsProvider hasSubscription={isPremium} />
          </div>
        )}

        {activeTab === 'branches' && (
          <div className="bg-white rounded-3xl border shadow-sm p-4 md:p-6" data-testid="tab-branches-content">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div>
                <h2 className="text-xl font-black text-[#000000]">Sucursales</h2>
                <p className="text-sm text-gray-500">Agrega otras ubicaciones de tu servicio.</p>
              </div>

              <Button
                onClick={() => {
                  setShowBranchForm(!showBranchForm);
                  resetBranchForm();
                }}
                className="bg-[#ffff00] hover:bg-[#e6e600] text-[#000000] rounded-xl font-bold"
                data-testid="add-branch-btn"
              >
                {showBranchForm ? 'Cancelar' : <><Plus className="w-4 h-4 mr-1" /> Agregar Sucursal</>}
              </Button>
            </div>

            <Link
              to={`/provider/${provider.provider_id}`}
              className="block mb-5 p-4 border-2 border-[#ffff00]/20 rounded-2xl bg-cyan-50/40 hover:bg-cyan-50 transition-colors"
              data-testid="main-profile-link"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                  <Building2 className="w-6 h-6 text-[#cccc00]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#cccc00] uppercase">Sede Principal</p>
                  <h3 className="font-black text-[#000000] truncate">{provider.business_name}</h3>
                  <p className="text-sm text-gray-500 truncate">
                    {[provider.address, provider.municipio, provider.region].filter(Boolean).join(', ') || 'Sin dirección'}
                  </p>
                </div>
                <Eye className="w-5 h-5 text-gray-400" />
              </div>
            </Link>

            {showBranchForm && (
              <div className="border-2 border-[#ffff00]/20 rounded-2xl p-5 mb-6 space-y-4 bg-cyan-50/30" data-testid="branch-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Nombre de la Sucursal *</label>
                    <Input
                      value={branchForm.business_name}
                      onChange={e => setBranchForm(prev => ({ ...prev, business_name: e.target.value }))}
                      placeholder="Ej: Clínica Veterinaria Curauma Sur - Sede Centro"
                      data-testid="branch-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Teléfono</label>
                    <Input
                      value={branchForm.phone}
                      onChange={e => setBranchForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+34 600 000 000"
                      data-testid="branch-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Dirección</label>
                  <Input
                    value={branchForm.address}
                    onChange={e => setBranchForm(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Av. Principal 456"
                    data-testid="branch-address"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Municipio</label>
                    <Input
                      value={branchForm.municipio}
                      onChange={e => setBranchForm(prev => ({ ...prev, municipio: e.target.value }))}
                      placeholder="Madrid"
                      data-testid="branch-municipio"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Comunidad autónoma</label>
                    <Input
                      value={branchForm.region}
                      onChange={e => setBranchForm(prev => ({ ...prev, region: e.target.value }))}
                      placeholder="Comunidad autónoma Comunidad de Madrid"
                      data-testid="branch-region"
                    />
                  </div>
                </div>

                <div className="bg-white border border-cyan-100 rounded-xl p-3 text-sm text-gray-600">
                  La sucursal heredará los servicios y galería de la sede principal.
                </div>

                <Button
                  onClick={createBranch}
                  disabled={savingBranch}
                  className="w-full bg-[#ffff00] hover:bg-[#e6e600] text-[#000000] py-5 font-black rounded-xl"
                  data-testid="save-branch-btn"
                >
                  {savingBranch ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : 'Crear Sucursal'}
                </Button>
              </div>
            )}

            {branches.length === 0 && !showBranchForm ? (
              <div className="text-center py-10 text-gray-500 border border-dashed rounded-2xl">
                <MapPin className="w-14 h-14 mx-auto mb-3 text-gray-300" />
                <p className="font-bold">No tienes sucursales registradas</p>
                <p className="text-sm mt-1">Agrega ubicaciones adicionales de tu servicio.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {branches.map(branch => (
                  <div
                    key={branch.provider_id}
                    className="border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:border-[#ffff00]/40 transition-colors"
                    data-testid={`branch-${branch.provider_id}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-12 h-12 bg-cyan-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#cccc00]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-black text-[#000000] truncate">{branch.business_name}</h3>
                        <p className="text-sm text-gray-500 truncate">
                          {[branch.address, branch.municipio, branch.region].filter(Boolean).join(', ') || 'Sin dirección'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Link to={`/provider/${branch.provider_id}`}>
                        <Button variant="outline" size="sm" className="rounded-xl">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500 border-red-200 hover:bg-red-50 rounded-xl"
                        onClick={() => deleteBranch(branch.provider_id)}
                        data-testid={`delete-branch-${branch.provider_id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default ProviderDashboard;
